import React, { useEffect, useMemo, useState } from "react";
import { IMaskInput } from "react-imask";
import {
  calcRevenue,
  compareDates,
  formatMoney,
  getLastRecognition,
  parseDate,
} from "./utils";
import Drawer from "./ui/Drawer";
import DatePicker from "./ui/DatePicker";
import ConfirmDialog from "./ui/ConfirmDialog";
import "./RecognitionForm.css";

const EMPTY_FORM = {
  date: "",
  projectId: "",
  organization: "",
  counterparty: "",
  contractId: "",
  article: "",
  contractAmount: null,
  readiness: 0,
};

function Field({ label, required, hint, error, children }) {
  return (
    <div className="rr-field">
      <span className="rr-field__label">
        {label}
        {required && <span className="rr-field__required">*</span>}
      </span>
      {children}
      {error && <div className="rr-field__error">{error}</div>}
      {hint && <div className="rr-field__hint">{hint}</div>}
    </div>
  );
}

function SelectField({ value, onChange, placeholder, options }) {
  return (
    <select
      className={`rr-select rr-select--field${value ? "" : " is-placeholder"}`}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

const toOptions = (values) => values.map((value) => ({ value, label: value }));

/** Форма создания признания; период берётся из настройки проекта или договора (config.periodBy) */
function RecognitionForm({ config, data, isOpen, recognitions, settings, onClose, onSubmit }) {
  const { texts } = config;
  const byContract = config.periodBy === "contract";
  const [form, setForm] = useState(EMPTY_FORM);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setIsConfirmOpen(false);
    }
  }, [isOpen]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const projectId = Number(form.projectId) || null;
  const contractId = Number(form.contractId) || null;

  const findSetting = (id) =>
    settings.find((item) => (byContract ? item.contractId : item.projectId) === id) || null;
  const setting = findSetting(byContract ? contractId : projectId);

  const isContractAllowed = (contract) => !byContract || Boolean(findSetting(contract.id));

  const projectOptions = useMemo(() => {
    const ids = byContract
      ? settings.map((item) => data.contractById[item.contractId]?.projectId).filter(Boolean)
      : settings.filter((item) => item.startDate).map((item) => item.projectId);
    return [...new Set(ids)]
      .map((id) => data.projectById[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, "ru"))
      .map((project) => ({ value: String(project.id), label: project.name }));
  }, [settings, byContract, data]);

  const contractOptions = data.contracts
    .filter((contract) => (!projectId || contract.projectId === projectId) && isContractAllowed(contract))
    .map((contract) => ({ value: String(contract.id), label: contract.name }));

  const getStartReadiness = (id) => getLastRecognition(recognitions, id)?.readiness ?? 0;

  /** Значения, которые подставляются при выборе договора */
  const getContractDefaults = (contract) => {
    const project = data.projectById[contract.projectId];
    const contractSetting = byContract ? findSetting(contract.id) : null;
    return {
      contractId: String(contract.id),
      organization:
        contractSetting?.organization || contract.organization || project?.organization || "",
      counterparty:
        contractSetting?.counterparty || contract.counterparty || project?.counterparty || "",
      article: contract.article || project?.article || "",
      contractAmount: contract.amount,
      readiness: getStartReadiness(contract.id),
    };
  };

  const handleProjectChange = (value) => {
    const project = data.projectById[Number(value)];
    if (!project) return;
    const contract = data.contracts.find(
      (item) => item.projectId === project.id && isContractAllowed(item)
    );
    setForm((prev) => ({
      ...prev,
      projectId: value,
      organization: project.organization,
      counterparty: project.counterparty || "",
      article: project.article || "",
      contractId: "",
      ...(contract ? getContractDefaults(contract) : {}),
    }));
  };

  const handleContractChange = (value) => {
    const contract = data.contractById[Number(value)];
    if (!contract) return;
    setForm((prev) => ({ ...prev, projectId: String(contract.projectId || ""), ...getContractDefaults(contract) }));
  };

  const lastRecognition = contractId ? getLastRecognition(recognitions, contractId) : null;
  const calc = calcRevenue(form.contractAmount, form.readiness, lastRecognition?.cumulative || 0);

  const dateValue = parseDate(form.date);
  let dateError = null;
  if (dateValue && setting) {
    const start = parseDate(setting.startDate);
    const end = parseDate(setting.endDate);
    if ((start && dateValue < start) || (end && dateValue > end)) {
      dateError = byContract
        ? "Дата вне периода признания договора"
        : "Дата вне периода признания проекта";
    }
  }
  if (!dateError && dateValue && lastRecognition && compareDates(form.date, lastRecognition.date) <= 0) {
    dateError = `Дата должна быть позже предыдущего признания (${lastRecognition.date})`;
  }

  const readinessError =
    lastRecognition && form.readiness < lastRecognition.readiness
      ? `Готовность не может быть ниже предыдущего признания (${lastRecognition.readiness}%)`
      : null;

  const hasCalc = form.contractAmount > 0 && form.readiness > 0;
  const isComplete =
    dateValue &&
    projectId &&
    form.organization &&
    form.counterparty &&
    contractId &&
    form.article &&
    form.contractAmount > 0;
  const canSubmit = Boolean(isComplete && !dateError && !readinessError && calc.current > 0);

  const periodHint = setting ? `Период признания: ${setting.startDate} — ${setting.endDate}` : null;
  const noOptionsHint =
    projectOptions.length === 0
      ? byContract
        ? "Нет договоров с настроенным периодом признания"
        : "Нет проектов с настроенным периодом признания"
      : null;

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    onSubmit({
      date: form.date,
      projectId,
      organization: form.organization,
      counterparty: form.counterparty,
      contractId,
      article: form.article,
      contractAmount: form.contractAmount,
      readiness: form.readiness,
      ...calc,
    });
  };

  const setReadiness = (value) => {
    const number = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    setField("readiness", number);
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        themeClass={config.themeClass}
        title={texts.formTitle}
        footer={
          <>
            <button type="button" className="rr-btn rr-btn--secondary" onClick={onClose}>
              Отмена
            </button>
            <button
              type="button"
              className="rr-btn rr-btn--primary"
              disabled={!canSubmit}
              onClick={() => setIsConfirmOpen(true)}
            >
              {texts.submit}
            </button>
          </>
        }
      >
        <h3 className="rr-form__section">Основные данные</h3>

        <Field label="Дата" required error={dateError}>
          <DatePicker
            value={form.date}
            onChange={(value) => setField("date", value)}
            invalid={Boolean(dateError)}
            highlight={setting ? { from: setting.startDate, to: setting.endDate } : null}
            ariaLabel="Дата признания"
          />
        </Field>

        <Field label="Проект" required hint={byContract ? noOptionsHint : periodHint || noOptionsHint}>
          <SelectField
            value={form.projectId}
            onChange={handleProjectChange}
            placeholder="Выберите проект из списка"
            options={projectOptions}
          />
        </Field>

        <Field label="Организация" required>
          <SelectField
            value={form.organization}
            onChange={(value) => setField("organization", value)}
            placeholder="Выберите организацию из списка"
            options={toOptions(data.organizations)}
          />
        </Field>

        <Field label="Контрагент" required>
          <SelectField
            value={form.counterparty}
            onChange={(value) => setField("counterparty", value)}
            placeholder="Выберите контрагента из списка"
            options={toOptions(data.counterparties)}
          />
        </Field>

        <Field label="Договор" required hint={byContract ? periodHint : null}>
          <SelectField
            value={form.contractId}
            onChange={handleContractChange}
            placeholder="Выберите договор из списка"
            options={contractOptions}
          />
        </Field>

        <Field label="Статья" required>
          <SelectField
            value={form.article}
            onChange={(value) => setField("article", value)}
            placeholder="Выберите статью из списка"
            options={toOptions(data.articles)}
          />
        </Field>

        <h3 className="rr-form__section">Данные для расчета</h3>

        <Field label="Сумма договора без НДС" required>
          <div className="rr-form__amount">
            <IMaskInput
              className="rr-form__input"
              mask={Number}
              scale={0}
              thousandsSeparator=" "
              min={0}
              unmask
              value={form.contractAmount == null ? "" : String(form.contractAmount)}
              onAccept={(value) => setField("contractAmount", value === "" ? null : Number(value))}
              placeholder="Укажите сумму договора без НДС"
            />
            <svg className="rr-form__amount-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path
                d="M8.5 2.5l3 3M1.5 12.5l.6-2.9 7.6-7.6a1.4 1.4 0 012 0l.8.8a1.4 1.4 0 010 2l-7.6 7.6-2.9.6z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </Field>

        <Field
          label="% готовности проекта"
          error={readinessError}
          hint={
            <>
              {lastRecognition && (
                <span className="rr-form__previous">
                  Предыдущее признание: {lastRecognition.readiness}% на {lastRecognition.date}
                </span>
              )}
              Укажите фактический процент готовности проекта на дату признания.
            </>
          }
        >
          <div className="rr-form__readiness">
            <div className="rr-form__percent">
              <input
                className="rr-form__input rr-form__input--percent"
                type="number"
                min={0}
                max={100}
                value={form.readiness}
                onChange={(event) => setReadiness(event.target.value)}
                aria-label="Процент готовности"
              />
              <span>%</span>
            </div>
            <input
              className="rr-form__slider"
              type="range"
              min={0}
              max={100}
              value={form.readiness}
              onChange={(event) => setReadiness(event.target.value)}
              style={{ "--rr-slider-fill": `${form.readiness}%` }}
              aria-label="Процент готовности, ползунок"
            />
          </div>
        </Field>

        {hasCalc && (
          <>
            <h3 className="rr-form__section">{texts.calcTitle}</h3>
            <div className="rr-form__calc">
              <div className="rr-form__calc-row">
                <span>Сумма договора без НДС</span>
                <span>{formatMoney(form.contractAmount)}</span>
              </div>
              <div className="rr-form__calc-row">
                <span>× Готовность</span>
                <span>{form.readiness}%</span>
              </div>
              <div className="rr-form__calc-row">
                <span>{texts.noun} накопительно</span>
                <span>{formatMoney(calc.cumulative)}</span>
              </div>
              <div className="rr-form__calc-row">
                <span>{texts.noun} за предыдущие периоды</span>
                <span>{formatMoney(calc.previous)}</span>
              </div>
              <div className="rr-form__calc-row is-total">
                <span>{texts.noun} за отчетный период</span>
                <span className={calc.current > 0 ? "is-positive" : "is-negative"}>
                  {formatMoney(calc.current)}
                </span>
              </div>
            </div>

            <div className="rr-form__note">{texts.note}</div>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        themeClass={config.themeClass}
        title={texts.confirmTitle}
        rows={[
          { label: "Проект", value: data.projectById[projectId]?.name || "—" },
          { label: "Дата", value: form.date },
          { label: "Готовность", value: `${form.readiness}%` },
          { label: "К признанию", value: formatMoney(calc.current), accent: true },
        ]}
        confirmLabel={texts.submit}
        onConfirm={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </>
  );
}

export default RecognitionForm;
